<template>
  <AtomsGlobalDialog :is-open="dialog?.isOpen" :is-message-container="dialog?.isMessageContainer" @close="close" class="w-[900px]">
    <div class="engraving">

      <strong class="block mb-8 pr-8 pb-8 uppercase font-bold text-2xl border-b border-neutral-200 tablet:px-8">{{ $t('Konfigurace laserového gravírování') }}</strong>

      <!-- step 1 - options -->

      <template v-if="step === 1">
        <div class="flex flex-wrap justify-center gap-12">
          <MoleculesGlobalWareImage :image-id="dialog?.data.wareImageId" :ware-name="dialog?.data.wareName" suffix="or" class="w-[200px] tablet:w-[300px]" />
          <div class="w-full tablet:flex-1">
            <div class="mb-7 text-2xl font-bold">{{ dialog?.data.wareName }}</div>
            <div class="mb-4 font-bold text-xl">{{ $t('Typ gravíru') }}</div>
            <div v-for="option in dialog?.data.serviceOptions" class="engraving__option" :class="{ 'engraving__option--selected': selectedServiceOption?.id === option.id }" @click="selectOption(option)">
              {{ option.name }} <span class="engraving__option__price">{{ locale.formatPrice(option.price) }}</span>
            </div>
          </div>
        </div>
      </template>

      <!-- step 2 - configurations -->

      <template v-else-if="step === 2">
        <div class="flex flex-wrap justify-center gap-12">
          <MoleculesGlobalWareImage :image-id="dialog?.data.wareImageId" :ware-name="dialog?.data.wareName" suffix="or" class="w-[200px] tablet:w-[300px]" />
          <div class="w-full tablet:flex-1">
            <div class="mb-7 text-2xl font-bold">{{ dialog?.data.wareName }}</div>

            <div class="space-y-4 text-lg text-neutral-400">

              <div v-if="configuration?.engravingType === 'combo'" class="font-bold text-xl text-neutral-500">{{ $t('Obrázek + Text') }}</div>

              <!-- image -->

              <div v-if="configuration?.engravingType === 'image'" class="font-bold text-xl text-neutral-500">{{ $t('Obrázek') }}</div>
              <div v-if="configuration?.engravingType === 'image' || configuration?.engravingType === 'combo'" class="text-base">
                <div class="min-h-[4rem]">
                  <div v-for="file in files" class="flex items-center gap-4 mb-2 min-h-[4rem]">
                    <AtomsImagesIcon icon="checked" size="xs" class="text-primary-500" />
                    <div v-html="$t('Soubor {0} byl úspěšně nahrán', [file.name])"></div>
                    <button class="underline text-error-600" @click="removeFile(file.name)">{{ $t('smazat') }}</button>
                  </div>
                  <MoleculesButtonsButton :ghost="true" icon-right="send" @click="fileUpload.click()" v-if="files.length === 0">{{ $t('Nahrát obrázek') }}</MoleculesButtonsButton>
                </div>
                <input type="file" ref="fileUpload" class="hidden" @change="handleFileUpload" />
                <div class="mt-4" v-html="$t('<strong>Obrázek musí být ve vektorech (obvykle formáty SVG nebo PDF)</strong>, nelze poslat klasickou fotku.')"></div>
              </div>

              <!-- text -->

              <div v-if="configuration?.engravingType === 'text' || configuration?.engravingType === 'combo'" class="space-y-4">
                <div class="mb-4 font-bold text-xl text-neutral-500">{{ $t('Text') }}</div>
                <div class="text-base" v-html="$t('Požadovaný text napište přesně tak, jak ho chcete vygravírovat. Rozlišujte malá a velká písmena, používejte diakritiku.')"></div>
                <MoleculesFormsValidatedField v-model="text" :label="$t('Požadovaný text')" :multi-line="true" :required="true" :placeholder="$t('Zde napište text')" class="!mt-6 text-base" :ref="(el) => elText = el" />
                <div class="text-base" v-html="$t('Popište nám co nejpřesněji, jakou má mít gravírování podobu. Jaký použít font (vybírejte z běžně dostupných), velikost fontu, zarovnání, co si přejete případně zpracovat tučně, apod.')"></div>
                <MoleculesFormsValidatedField v-model="requirements" :label="$t('Detailní popis')" :multi-line="true" :required="true" :placeholder="$t('Zde napište požadavky')" :ref="(el) => elRequirements = el" class="!mt-6 text-base" />
              </div>

              <!-- summary -->

              <div class="text-base" v-html="$t('Než se pustíme do práce, tak vám zavoláme a veškeré detaily si s vámi ještě jednou projdeme a dořešíme.')"></div>

              <div class="text-right">
                {{ $t('Dostupnost') }}: <strong>{{ $t('obvykle do {0} prac. dnů', [selectedServiceOption.deliveryPeriod]) }}</strong><br />
                {{ $t('Cena') }}: <strong class="text-xl">{{ locale.formatPrice(selectedServiceOption.price) }}</strong>
              </div>

            </div>

          </div>
        </div>
      </template>

      <!-- buttons -->

      <div class="flex flex-wrap gap-4 justify-center mt-8 pt-8 border-t border-neutral-200 tablet:justify-between tablet:mt-10 tablet:pt-10">
        <div class="flex flex-wrap gap-2 justify-end w-full tablet:flex-1 tablet:order-[1]">
          <MoleculesButtonsButton :arrow-right="true" v-if="step === 2" class="w-full tablet:w-auto tablet:order-[1]" @click="submit">{{ $t(dialog?.data.cartItemData ? 'Přidat do košíku' : 'Přikoupit gravírování') }}</MoleculesButtonsButton>
          <Transition v-if="step === 1">
            <MoleculesButtonsButton :arrow-right="true" v-if="step === 1 && selectedServiceOption" class="w-full tablet:w-auto" @click="nextStep">{{ $t('Další krok') }}</MoleculesButtonsButton>
          </Transition>
          <MoleculesButtonsButton :arrow-left="true" :ghost="true" v-if="step === 2" @click="step = 1" class="w-full tablet:order-[0] tablet:w-auto">{{ $t('Předchozí krok') }}</MoleculesButtonsButton>
        </div>
        <MoleculesButtonsButton :arrow-left="true" :black="true" @click="close" class="tablet:order-[0]">{{ $t('Nechci gravírování') }}</MoleculesButtonsButton>
      </div>

    </div>
  </AtomsGlobalDialog>
</template>
<script setup>

const i18n = useI18n();
const locale = useLocale();
const appConfig = useAppConfig();
const analytics = useBaseAnalytics();

const dialog = computed(() => useDialog().get('engraving'));

const emit = defineEmits(['close']);

const text = ref('');
const requirements = ref('');

const elText = ref(null);
const elRequirements = ref(null);

//#region file upload

const fileUpload = ref(null);
const files = ref([]);

const handleFileUpload = event => {

  let uploadedFiles = Array.from(event.target.files);

  if (uploadedFiles.some(file => file.size > 20 * 1024 * 1024)) {
    const name = uploadedFiles.find(file => file.size > 20 * 1024 * 1024).name;
    useMessage().show(i18n.t('Soubor "{0}" je příliš velký - maximální velikost souboru je 20 MB', [name]), null, true);

    uploadedFiles = uploadedFiles.filter(file => file.size <= 20 * 1024 * 1024);
  }
  else if (uploadedFiles.some(file => !appConfig.engravingAllowedFileTypes.includes(getFileExtensionFromName(file.name)))) {
    const name = uploadedFiles.find(file => !appConfig.engravingAllowedFileTypes.includes(getFileExtensionFromName(file.name))).name;
    useMessage().show(i18n.t('Soubor "{0}" není ve správném formátu - povolené jsou pouze vektorové soubory', [name]), null, true);

    uploadedFiles = uploadedFiles.filter(file => appConfig.engravingAllowedFileTypes.includes(getFileExtensionFromName(file.name)));
  }

  files.value.push(...uploadedFiles);
};

const getFileExtensionFromName = (fileName) => fileName.split('.').pop().toLowerCase();

const upload = async () => {

  const fileUids = [];

  for (var i = 0; i < files.value.length; i++) {
    const formData = new FormData();
    formData.append('file', files.value[i]);

    const fileUid = (await useApiFetch(`/api/mutations/file`, {
      method: 'POST',
      body: formData
    })).file.upload.fileUid;

    files.value[i].uid = fileUid;
  }

  return fileUids;
}

const removeFile = name => {
  files.value = files.value.filter(file => file.name !== name);
};

//#endregion

const reset = () => {
  step.value = 1;
  selectedServiceOption.value = null;
  text.value = '';
  requirements.value = '';
  files.value = [];
}

const close = (withEngraving) => {

  if (withEngraving) {
    analytics.pushEvent('user_interaction', {
      interaction_name: 'engraving_added',
      engraving_option: selectedServiceOption.value.name,
      engraving_source: dialog.value.data.source,
      engraving_item_name: dialog.value.data.wareName,
      engraving_item_category: dialog.value.data.wareCategory
    });
  }
  else {
    analytics.pushEvent('user_interaction', {
      interaction_name: 'engraving_closed_without_selection',
      engraving_source: dialog.value.data.source,
      engraving_item_name: dialog.value.data.wareName,
      engraving_item_category: dialog.value.data.wareCategory
    });
  }


  useDialog().close('engraving');
  emit('close', { cartItem: dialog.value.data.cartItemData, withEngraving });
  reset();
}

const validate = async () => {

  if ((configuration.value?.engravingType === 'image' || configuration.value?.engravingType === 'combo') && files.value.length === 0) {
    useMessage().show(i18n.t('Vyberte obrázek ke gravírování'), null, true);
    return false;
  }

  if ((configuration.value?.engravingType === 'text' || configuration.value?.engravingType === 'combo') && !(await elText.value.validate())) {
    useMessage().show(i18n.t('Zadejte text ke gravírování'), null, true);
    return false;
  }

  if ((configuration.value?.engravingType === 'text' || configuration.value?.engravingType === 'combo') && !(await elRequirements.value.validate())) {
    useMessage().show(i18n.t('Zadejte popis, jak má gravírování vypadat'), null, true);
    return false;
  }

  return true;
}

const selectOption = (option) => {
  selectedServiceOption.value = option;
}

const nextStep = () => {

  step.value = 2;

  analytics.pushEvent('user_interaction', {
    interaction_name: 'engraving_option_selected',
    engraving_option: selectedServiceOption.value.name,
    engraving_source: dialog.value.data.source,
    engraving_item_name: dialog.value.data.wareName,
    engraving_item_category: dialog.value.data.wareCategory
  });
}

const submit = async () => {

  if (!(await validate())) return;

  let presentationText = `${i18n.t('Produkt')}: ${dialog.value.data.wareName}<br />\n${i18n.t('Typ gravíru')}: ${selectedServiceOption.value.name}`;
  let serviceConfiguration = {};

  if (configuration.value.engravingType === 'image' || configuration.value.engravingType === 'combo') {

    await upload();

    presentationText += `<br />\n${i18n.t('Obrázek')}: ${files.value[0].name}`;
    serviceConfiguration.imageFile = files.value[0].uid;
    serviceConfiguration.originalFileName = files.value[0].name;
  }

  if (configuration.value.engravingType === 'text' || configuration.value.engravingType === 'combo') {
    presentationText += `<br />\n${i18n.t('Text')}: ${text.value}<br />\n${i18n.t('Detailní popis')}: ${requirements.value}`;
    serviceConfiguration.text = text.value;
    serviceConfiguration.requirements = requirements.value;
  }

  // at the time of submitting the cartItem is already present, so add engraving directly to the cartItem and the presentation will be updated automatically
  await useCart().addServiceToCartItem(dialog.value.data.cartItemData.cartItemId, dialog.value.data.serviceId, selectedServiceOption.value.id, presentationText, JSON.stringify(serviceConfiguration));

  // if added additionally within addedToCart dialog, update it (hide engraving button, show service)
  if (useDialog().get('addedToCart')?.isOpen) {
    useDialog().get('addedToCart').data.includeLastService = true;
  }

  close(true);
}

const step = ref(1);

const selectedServiceOption = ref(null);
const configuration = computed(() => selectedServiceOption.value?.configuration ? JSON.parse(selectedServiceOption.value?.configuration) : null);

watch(() => dialog.value?.isOpen, (newVal) => {
  if (newVal) {
    analytics.pushEvent('user_interaction', {
      interaction_name: 'engraving_open',
      engraving_source: dialog.value.data.source,
      engraving_item_name: dialog.value.data.wareName,
      engraving_item_category: dialog.value.data.wareCategory
    });
  }
});

</script>
<style lang="postcss">
.engraving {

  @apply p-4 tracking-normal;

  &__option {

    @apply flex justify-between items-center gap-4 mb-1 pl-4 pr-2 py-2 text-xl border border-neutral-100 bg-neutral-100 cursor-pointer transition-all ease-in-out;

    &__price {
      @apply inline-block min-w-[100px] p-2 font-bold text-center text-neutral-50 bg-neutral-500 transition-all ease-in-out;
    }

    &:hover,
    &--selected {
      @apply border-primary-500;

      .engraving__option__price {
        @apply bg-primary-500;
      }
    }
  }
}
</style>